import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { PCB_CORE_URL } from "../config";
import { BankTransferModal, OrderValidationModal } from "./quotes";
import "./orderTracking.css";
import { authHeader, authenticatedFetch } from "../authSession";

const API_BASE = (PCB_CORE_URL || "").replace(/\/+$/, "");

function fmtMoney(v, c = "EUR") {
  const n = Number(v || 0);
  const raw = String(c || "EUR").trim();
  const currency = raw === "" ? "EUR" : raw.toUpperCase();
  try {
    return new Intl.NumberFormat("fr-FR", { style: "currency", currency }).format(n);
  } catch {
    return new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR" }).format(n);
  }
}

function normalizeStatus(v) {
  return String(v || "").trim().toLowerCase();
}

const STATUS_FR = {
  draft: "Brouillon",
  pending: "En attente",
  validated: "Validé",
  rejected: "Rejeté",
  paid: "Payé",
  in_production: "En production",
  shipped: "Expédié",
  completed: "Terminé",
  cancelled: "Annulé",
  in_progress: "En cours",
  processing: "En cours",
};

function statusFr(v) {
  const s = normalizeStatus(v);
  return STATUS_FR[s] || String(v || "-");
}

function statusClass(v) {
  const s = normalizeStatus(v);
  if (["confirmée", "commande confirmée"].includes(s)) return "ot-badge ot-badge--paid";
  if (["envoyé", "accepté", "prête à confirmer"].includes(s)) return "ot-badge ot-badge--progress";
  if (["non confirmée", "à renvoyer"].includes(s)) return "ot-badge ot-badge--neutral";
  if (["paid", "payé", "payée"].includes(s)) return "ot-badge ot-badge--paid";
  if (["draft", "brouillon"].includes(s)) return "ot-badge ot-badge--draft";
  if (["delivered", "livré", "livrée"].includes(s)) return "ot-badge ot-badge--delivered";
  if (["shipped", "expédié", "expédiée"].includes(s)) return "ot-badge ot-badge--shipped";
  if (["in_progress", "processing", "en_cours", "en cours"].includes(s)) return "ot-badge ot-badge--progress";
  return "ot-badge ot-badge--neutral";
}

function quoteDisplayStatus(item) {
  const acceptanceUi = item?.customer_acceptance_ui || {};
  const invoicePaymentStatus = normalizeStatus(item?.payment_invoice_status || "");
  if (Boolean(item?.payment_is_paid) || invoicePaymentStatus === "manual_paid_invoice_sent") return "Payé";
  if (Boolean(item?.is_validated_by_customer)) return "Accepté";
  if (String(acceptanceUi?.estimate_sent_at || "").trim()) return "Envoyé";
  if (String(acceptanceUi?.estimate_id || "").trim()) return "Préparé";
  return statusFr(item?.quote_status || "-");
}

function orderDisplayStatus(item) {
  const emailStatus = String(item?.order_confirmation_email_status || "").trim().toLowerCase();
  const acceptedByCustomer = Boolean(item?.is_validated_by_customer);

  if (emailStatus === "sent") return "Confirmée";
  if (emailStatus === "pending_acceptance") return "Prête à confirmer";
  if (emailStatus === "error") return "À renvoyer";
  if (!acceptedByCustomer) return "Non confirmée";
  if (emailStatus === "missing_file") return "Non confirmée";
  return "Non confirmée";
}
async function downloadQuotePdf(quoteId) {
  const res = await authenticatedFetch(`${API_BASE}/user/quotes/${quoteId}/pdf/`, {
    method: "GET",
    headers: {
      ...authHeader(),
    },
  });

  const contentType = (res.headers.get("content-type") || "").toLowerCase();
  if (!res.ok) {
    let detail = `HTTP ${res.status}`;
    try {
      if (contentType.includes("application/json")) {
        const err = await res.json();
        detail = err?.detail || err?.message || detail;
      } else {
        const t = await res.text();
        if (t) detail = t;
      }
    } catch (_e) {}
    throw new Error(detail);
  }

  const blob = await res.blob();
  if (!blob || blob.size === 0) {
    throw new Error("PDF vide");
  }

  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `quote_${quoteId}.pdf`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export default function OrderTrackingPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [data, setData] = useState(null);
  const [payingBankTransfer, setPayingBankTransfer] = useState(false);
  const [bankOpen, setBankOpen] = useState(false);
  const [bankData, setBankData] = useState(null);
  const [acceptingQuoteId, setAcceptingQuoteId] = useState(null);
  const [orderValidationQuoteId, setOrderValidationQuoteId] = useState(null);

  useEffect(() => {
    let alive = true;

    async function load() {
      setErr("");
      try {
        const res = await authenticatedFetch(`${API_BASE}/user/order-tracking/`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            ...authHeader(),
          },
        });

        if (!res.ok) {
          const t = await res.text().catch(() => "");
          throw new Error(`HTTP ${res.status} ${t}`.trim());
        }

        const payload = await res.json();
        if (!alive) return;
        setData(payload || null);
      } catch (e) {
        if (!alive) return;
        setErr(e?.message || "Erreur de chargement.");
      } finally {
        if (!alive) return;
        setLoading(false);
      }
    }

    load();
    return () => {
      alive = false;
    };
  }, []);

  async function handlePayBankTransfer(quoteId) {
    if (!quoteId) return;
    setErr("");
    setPayingBankTransfer(true);
    try {
      const res = await authenticatedFetch(`${API_BASE}/user/quotes/${quoteId}/bank-transfer/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...authHeader(),
        },
        body: JSON.stringify({}),
      });

      const contentType = (res.headers.get("content-type") || "").toLowerCase();
      const payload = contentType.includes("application/json")
        ? await res.json().catch(() => null)
        : { detail: await res.text().catch(() => "") };

      if (!res.ok) {
        throw new Error(payload?.detail || `HTTP ${res.status}`);
      }
      if (!payload?.reference) {
        throw new Error("Référence de virement manquante.");
      }

      setBankData(payload);
      setBankOpen(true);
      return true;
    } catch (e) {
      setErr(e?.message || "Erreur virement.");
      return false;
    } finally {
      setPayingBankTransfer(false);
    }
  }

  function applyAcceptedQuoteToTrackingState(quoteId, payload) {
    setData((prev) => {
      if (!prev) return prev;
      const updatedQuote = payload?.quote || {};
      const updatedMeta = updatedQuote?.pricing_meta || {};
      const updatedAcceptanceUi = payload?.customer_acceptance_ui || updatedQuote?.customer_acceptance_ui || {};
      const updatedOrderConfirmation = payload?.order_confirmation_email || {};

      const nextItems = (Array.isArray(prev.items) ? prev.items : []).map((it) => {
        if (it?.quote_id !== quoteId) return it;
        return {
          ...it,
          quote_status: updatedQuote?.status || it?.quote_status,
          total: updatedQuote?.total ?? it?.total,
          total_ttc: updatedQuote?.total_ttc ?? it?.total_ttc,
          is_validated_by_customer: Boolean(updatedQuote?.is_validated_by_customer),
          customer_acceptance_ui: updatedAcceptanceUi,
          order_confirmation_email_status: String(
            updatedMeta?.customer_order_confirmation_email_status
            || updatedOrderConfirmation?.status
            || it?.order_confirmation_email_status
            || ""
          ).trim().toLowerCase(),
          order_confirmation_email_sent_at:
            updatedMeta?.customer_order_confirmation_email_sent_at
            || it?.order_confirmation_email_sent_at
            || "",
          order_confirmation_email_error:
            updatedMeta?.customer_order_confirmation_email_error
            || (updatedOrderConfirmation?.status === "error" ? updatedOrderConfirmation?.detail : "")
            || "",
        };
      });

      const nextLatest = nextItems[0] || prev.latest || null;
      return {
        ...prev,
        items: nextItems,
        latest: nextLatest,
        quote_id: nextLatest?.quote_id ?? prev.quote_id,
        project_id: nextLatest?.project_id ?? prev.project_id,
        project_title: nextLatest?.project_title ?? prev.project_title,
        quote_status: nextLatest?.quote_status ?? prev.quote_status,
        total: nextLatest?.total ?? prev.total,
        total_ttc: nextLatest?.total_ttc ?? prev.total_ttc,
        is_validated_by_customer: nextLatest?.is_validated_by_customer ?? prev.is_validated_by_customer,
        customer_acceptance_ui: nextLatest?.customer_acceptance_ui ?? prev.customer_acceptance_ui,
        order_confirmation_email_status:
          nextLatest?.order_confirmation_email_status ?? prev.order_confirmation_email_status,
        order_confirmation_email_sent_at:
          nextLatest?.order_confirmation_email_sent_at ?? prev.order_confirmation_email_sent_at,
        order_confirmation_email_error:
          nextLatest?.order_confirmation_email_error ?? prev.order_confirmation_email_error,
      };
    });
  }

  async function handleAcceptQuote(quoteId) {
    if (!quoteId) return;
    setErr("");
    setAcceptingQuoteId(quoteId);
    try {
      const res = await authenticatedFetch(`${API_BASE}/user/quotes/${quoteId}/accept/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...authHeader(),
        },
        body: JSON.stringify({ professional_confirmation: true }),
      });

      const contentType = (res.headers.get("content-type") || "").toLowerCase();
      const payload = contentType.includes("application/json")
        ? await res.json().catch(() => null)
        : { detail: await res.text().catch(() => "") };

      if (!res.ok) {
        throw new Error(payload?.detail || `HTTP ${res.status}`);
      }

      applyAcceptedQuoteToTrackingState(quoteId, payload || {});
      setOrderValidationQuoteId(null);
      alert("Devis validé. Le bon de commande vous sera envoyé par email.");
    } catch (e) {
      setErr(e?.message || "Erreur de validation du devis.");
    } finally {
      setAcceptingQuoteId(null);
    }
  }

  const items = useMemo(() => {
    if (!data?.exists) return [];
    if (Array.isArray(data?.items) && data.items.length) return data.items;
    return [data];
  }, [data]);

  const headerSubtitle = useMemo(() => {
    if (!items.length) return "Aucun devis actif";
    return `${items.length} devis disponibles`;
  }, [items]);

  if (loading) {
    return (
      <section className="ot-page">
        <div className="ot-state">Chargement du suivi de commande...</div>
      </section>
    );
  }

  if (err) {
    return (
      <section className="ot-page">
        <div className="ot-state ot-state--error">{err}</div>
      </section>
    );
  }

  if (!data?.exists || !items.length) {
    return (
      <section className="ot-page">
        <div className="ot-state">Aucun devis pour le moment.</div>
      </section>
    );
  }

  return (
    <section className="ot-page">
      <header className="ot-hero">
        <div>
          <h2 className="ot-title">Mes Devis & Suivi Commande</h2>
          <p className="ot-subtitle">{headerSubtitle}</p>
        </div>
      </header>

      <div className="ot-list">
        {items.map((it, i) => {
          const tracking = it?.tracking || {};
          const stage = it?.payment_flow_stage || "";
          const paymentMode = it?.payment_mode || "immediate_payment";
          const acceptanceUi = it?.customer_acceptance_ui || {};
          const acceptedByCustomer = Boolean(it?.is_validated_by_customer);
          const canAcceptQuote = Boolean(acceptanceUi?.can_accept) && !acceptedByCustomer;
          const orderConfirmationEmailStatus = String(it?.order_confirmation_email_status || "").trim().toLowerCase();
          const orderConfirmationEmailError = String(it?.order_confirmation_email_error || "").trim();
          const quoteDisplayLabel = quoteDisplayStatus(it);
          const orderDisplayLabel = orderDisplayStatus(it);
          const quoteStatusValue = normalizeStatus(it?.quote_status || "");
          const invoicePaymentStatus = normalizeStatus(it?.payment_invoice_status || "");
          const paymentIsPaid = Boolean(it?.payment_is_paid || it?.is_paid)
            || invoicePaymentStatus === "manual_paid_invoice_sent"
            || ["paid", "payé", "payee", "payée"].includes(quoteStatusValue);
          const lines = Array.isArray(it?.lines) ? it.lines : [];
          const showTracking = acceptedByCustomer && [
            "bank_transfer_tracking_and_invoice",
            "bank_transfer_tracking",
            "immediate_payment_tracking_and_invoice",
            "immediate_payment_tracking",
          ].includes(stage);
          const canPayNow = (
            acceptedByCustomer
            && paymentMode === "immediate_payment"
            && !paymentIsPaid
            && !["draft", "awaiting_validation", "immediate_payment_manual_paid_invoice_required"].includes(stage)
          );
          const showBankTransferCta = acceptedByCustomer && paymentMode === "bank_transfer" && !["draft", "awaiting_validation", "bank_transfer_paid"].includes(stage);
          const awaitingInvoice = acceptedByCustomer && ["immediate_payment_send_invoice", "bank_transfer_paid"].includes(stage);

          return (
            <article className="ot-card" key={`${it?.quote_id || "q"}-${i}`}>
              <div className="ot-card-head">
                <h3 className="ot-card-title">Devis {it?.quote_id || "-"} : {it?.project_title || `Projet #${it?.project_id || "-"}`}</h3>
                <div className="ot-statuses">
                  <span className={statusClass(quoteDisplayLabel)}>Devis: {quoteDisplayLabel}</span>
                  <span className={statusClass(orderDisplayLabel)}>Commande: {orderDisplayLabel}</span>
                </div>
              </div>

              <div className="ot-grid">
                <div>
                  <div className="ot-kv"><span>Sous-total</span><strong>{fmtMoney(it?.subtotal, it?.currency || "EUR")}</strong></div>
                  {it?.discount_kind && it.discount_kind !== "none" && Number(it?.discount_value) > 0 && (
                    <div className="ot-kv ot-kv--discount">
                      <span>Remise</span>
                      <strong style={{ color: "#4ade80" }}>
                        {it.discount_kind === "pct"
                          ? `-${Number(it.discount_value).toFixed(0)} %`
                          : `-${fmtMoney(it.discount_value, it?.currency || "EUR")}`}
                      </strong>
                    </div>
                  )}
                  <div className="ot-kv ot-kv--total"><span>Total HT</span><strong>{fmtMoney(it?.total, it?.currency || "EUR")}</strong></div>
                  {it?.total_ttc != null && (
                    <div className="ot-kv"><span>Total TTC</span><strong>{fmtMoney(it?.total_ttc, it?.currency || "EUR")}</strong></div>
                  )}
                </div>

                {showTracking && (
                  <div>
                    <div className="ot-kv"><span>Transporteur</span><strong>{tracking?.carrier || "-"}</strong></div>
                    <div className="ot-kv"><span>N° de suivi</span><strong className="ot-mono">{tracking?.tracking_number || "-"}</strong></div>
                    <div className="ot-kv">
                      <span>Lien</span>
                      {tracking?.tracking_url ? (
                        <a href={tracking.tracking_url} target="_blank" rel="noreferrer" className="ot-link">Ouvrir le suivi</a>
                      ) : (
                        <strong>-</strong>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {!acceptedByCustomer && (
                <div className="ot-pay-ready">
                  <div className="ot-pay-ready__text">
                    {canAcceptQuote
                      ? "En validant le devis, une confirmation de commande vous sera envoyée par email."
                      : (acceptanceUi?.helper_text || "Le devis doit être accepté avant la suite du parcours.")}
                  </div>
                  {canAcceptQuote && (
                    <button
                      className="ot-pay-ready__btn"
                      onClick={() => setOrderValidationQuoteId(it?.quote_id)}
                      disabled={acceptingQuoteId === it?.quote_id}
                    >
                      {acceptingQuoteId === it?.quote_id ? "Validation..." : (acceptanceUi?.button_label || "Valider le devis")}
                    </button>
                  )}
                </div>
              )}

              {acceptedByCustomer && (
                <div className="ot-pay-ready">
                  <div className="ot-pay-ready__text">
                    {orderConfirmationEmailStatus === "sent"
                      ? "Commande confirmée : le bon de commande a été envoyé par email."
                      : orderConfirmationEmailStatus === "missing_file"
                        ? "Commande confirmée : le backoffice n'a pas encore téléversé le bon de commande."
                        : orderConfirmationEmailStatus === "error"
                          ? `Commande confirmée : l'envoi email du bon de commande a échoué${orderConfirmationEmailError ? ` (${orderConfirmationEmailError})` : "."}`
                          : "le devis a bien été accepté."}
                  </div>
                </div>
              )}

              {canPayNow && (
                <div className="ot-pay-ready">
                  <div className="ot-pay-ready__text">Ce devis est validé par le backoffice et prêt à être payé.</div>
                  <button className="ot-pay-ready__btn" onClick={() => navigate(`/app/projects/${it?.project_id}/steps`)}>
                    Aller au projet pour payer
                  </button>
                </div>
              )}

              {awaitingInvoice && (
                <div className="ot-pay-ready">
                  <div className="ot-pay-ready__text">Votre paiement a bien été reçu. La facture est en cours de préparation, le suivi de livraison apparaîtra dès son envoi.</div>
                </div>
              )}

              {showBankTransferCta && (
                <div className="ot-pay-ready">
                  <div className="ot-pay-ready__text">Paiement par virement bancaire : retrouvez les coordonnées ci-dessous.</div>
                  <button className="ot-pay-ready__btn" onClick={() => handlePayBankTransfer(it?.quote_id)} disabled={payingBankTransfer}>
                    {payingBankTransfer ? "Chargement..." : "Voir les coordonnées de virement"}
                  </button>
                </div>
              )}

              <div style={{ marginTop: 10 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                  <h4 className="ot-card-title" style={{ marginBottom: 0 }}>Lignes du devis</h4>
                  {/* {["paid", "payé", "payée", "payé", "payée"].includes(normalizeStatus(quoteStatus)) && (
                    <button
                      className="ot-link"
                      style={{ border: "none", background: "transparent", cursor: "pointer", fontWeight: 700 }}
                      onClick={async () => {
                        try {
                          await downloadQuotePdf(it?.quote_id);
                        } catch (e) {
                          alert(e?.message || "Erreur téléchargement PDF");
                        }
                      }}
                    >
                      Télécharger PDF
                    </button>
                  )} */}
                </div>

                {!lines.length ? (
                  <div className="ot-empty">Aucune ligne disponible.</div>
                ) : (
                  <div className="ot-lines">
                    {lines.map((l, idx) => (
                      <div className="ot-line" key={`${l.label || "line"}-${idx}`}>
                        <span className="ot-line-label">{l.label || "Ligne"}</span>
                        <strong className="ot-line-amount">{fmtMoney(l.amount, it?.currency || "EUR")}</strong>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </article>
          );
        })}
      </div>
      <BankTransferModal
        open={bankOpen}
        onClose={() => setBankOpen(false)}
        data={bankData}
      />
      <OrderValidationModal
        open={Boolean(orderValidationQuoteId)}
        accepting={Boolean(acceptingQuoteId)}
        onClose={() => setOrderValidationQuoteId(null)}
        onConfirm={() => handleAcceptQuote(orderValidationQuoteId)}
      />
    </section>
  );
}
